import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {MutationObserver | undefined} */
  #soldOutLastObserver;

  #soldOutLastScheduled = false;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    if (this.#isCollectionResults()) {
      this.#setupSoldOutLast();
    }
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#soldOutLastObserver?.disconnect();
    super.disconnectedCallback();
  }

  /**
   * Keep available products ahead of sold-out products on collection pages.
   * The infinite-scroll sentinel is moved between both groups so the next
   * page is requested before shoppers reach sold-out items. DOM card order
   * itself stays untouched, preserving the pagination component's page logic.
   */
  #setupSoldOutLast() {
    this.#applySoldOutLast();

    this.#soldOutLastObserver?.disconnect();
    this.#soldOutLastObserver = new MutationObserver(this.#scheduleSoldOutLast);
    this.#soldOutLastObserver.observe(this, { childList: true, subtree: true });
  }

  #isCollectionResults() {
    return Array.from(this.classList).some((className) => className.startsWith('collection-handle-'));
  }

  #scheduleSoldOutLast = () => {
    if (this.#soldOutLastScheduled) return;
    this.#soldOutLastScheduled = true;

    requestAnimationFrame(() => {
      this.#soldOutLastScheduled = false;
      this.#applySoldOutLast();
    });
  };

  #applySoldOutLast() {
    const { grid, viewMoreNext } = this.refs;
    if (!grid) return;

    const items = grid.querySelectorAll(':scope > [ref="cards[]"]');
    for (const item of items) {
      if (!(item instanceof HTMLElement)) continue;

      const card = item.querySelector('product-card[data-vda-available]');
      const isSoldOut = card?.getAttribute('data-vda-available') === 'false';
      item.style.order = isSoldOut ? '2' : '0';
    }

    if (viewMoreNext instanceof HTMLElement) {
      if (viewMoreNext.parentElement !== grid) {
        grid.append(viewMoreNext);
      }

      viewMoreNext.style.order = '1';
      viewMoreNext.style.gridColumn = '1 / -1';
      viewMoreNext.style.display = 'block';
      viewMoreNext.style.width = '100%';
      viewMoreNext.style.height = '1px';
      viewMoreNext.style.minHeight = '1px';
      viewMoreNext.style.margin = '0';
      viewMoreNext.style.padding = '0';
      viewMoreNext.style.pointerEvents = 'none';
      viewMoreNext.setAttribute('aria-hidden', 'true');
      viewMoreNext.dataset.vdaSoldOutBoundary = 'true';
    }
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
